/**
 * The runtime error contract for social account linking.
 *
 * Lives outside `link-social.types.ts` (which stays runtime-free) and outside
 * both platform files, so `link-social.web.ts` and `link-social.native.ts` can
 * share it without importing each other.
 *
 * Callers branch on `reason`, never on `message`: Better Auth owns the wording
 * and has reworded these strings between releases, while the codes are part of
 * its documented error surface.
 */

import type { LinkSocialFailureReason } from './link-social.types';

/** Better Auth codes meaning the provider identity belongs to someone else. */
const ALREADY_LINKED_CODES: readonly string[] = [
  // Raised by `/link-social` when the account row cannot be created because the
  // provider identity is already taken (`account.mjs` LINKING_FAILED).
  'LINKING_FAILED',
  'SOCIAL_ACCOUNT_ALREADY_LINKED',
  'LINKED_ACCOUNT_ALREADY_EXISTS',
  // Raised by the OAuth callback on the web redirect path.
  'ACCOUNT_ALREADY_LINKED_TO_DIFFERENT_USER',
];

/** Better Auth codes meaning the provider email does not match this account. */
const DIFFERENT_EMAIL_CODES: readonly string[] = [
  'LINKING_DIFFERENT_EMAILS_NOT_ALLOWED',
];

const DEFAULT_MESSAGES: Record<LinkSocialFailureReason, string> = {
  'already-linked': 'That account is already connected to another Flavrly account.',
  'different-email': 'That account uses a different email address than this one.',
  unknown: 'Could not connect that account.',
};

/** A link attempt that failed for a reason the caller can act on. */
export class LinkSocialError extends Error {
  readonly reason: LinkSocialFailureReason;

  constructor(message: string, reason: LinkSocialFailureReason) {
    super(message);
    this.name = 'LinkSocialError';
    this.reason = reason;
  }
}

/**
 * Classify a Better Auth error code. The web callback returns its code
 * lower-cased in a query param while the client returns it upper-cased, so
 * normalize before comparing.
 */
function toReason(code: string | undefined): LinkSocialFailureReason {
  if (!code) return 'unknown';
  const normalized = code.toUpperCase();
  if (ALREADY_LINKED_CODES.includes(normalized)) return 'already-linked';
  if (DIFFERENT_EMAIL_CODES.includes(normalized)) return 'different-email';
  return 'unknown';
}

/** Build a `LinkSocialError` from Better Auth's `{ code, message }` error body. */
export function toLinkSocialError(
  error: { code?: string; message?: string } | undefined
): LinkSocialError {
  const reason = toReason(error?.code);
  return new LinkSocialError(error?.message || DEFAULT_MESSAGES[reason], reason);
}
